import { BlurFilter, Container, DisplayObject, Graphics, IDestroyOptions, Rectangle, Renderer, SCALE_MODES, Sprite } from "pixi.js";
import { ViewableLayer } from "../../../scene/layer/ViewableLayer";
import { IFocusable } from "./IFocusable";
import { ICanvasScaler } from "../../../canvas/ICanvasScaler";
import { FixedSizeContainer } from "../common/components/FixedSizeContainer";
import { Scales } from "../../../canvas/CanvasScaler";

export class CityverseTycoonViewableLayer extends ViewableLayer {
    private focusedChildren: Container;
    private bluredChildren: Container;
    private canvasScaler: ICanvasScaler;
    private maskObject: Graphics;
    private readonly fixedWidth: number;
    private readonly fixedHeight: number;
    private readonly renderer: Renderer;

    public constructor(canvasScaler: ICanvasScaler, renderer: Renderer) {
        super();
        this.canvasScaler = canvasScaler;
        this.renderer = renderer;
        const size = this.canvasScaler.getSize();
        this.fixedWidth = this.canvasScaler.referenceWidth;
        this.fixedHeight = this.canvasScaler.referenceHeight;
        this.focusedChildren = new FixedSizeContainer(this.fixedWidth, this.fixedHeight);
        this.bluredChildren = new FixedSizeContainer(this.fixedWidth, this.fixedHeight);
        this.bluredChildren.filters = [new BlurFilter(10)];
        this.bluredChildren.filterArea = new Rectangle(0, 0, this.canvasScaler.browserWidth, this.canvasScaler.browserHeight);
        this.addChildAt(this.focusedChildren, 0);
        this.addChildAt(this.bluredChildren, 0);
        this.maskObject = new Graphics().beginFill(0xff0000).drawRect(0, 0, this.fixedWidth, this.fixedHeight);
        this.mask = this.maskObject;
        this.addChildAt(this.maskObject, 2);
        this.onResize(size);
        this.canvasScaler.onResize.add(this.onResize, this);
    }
    
    public override destroy(options?: boolean | IDestroyOptions): void {
        this.canvasScaler.onResize.remove(this.onResize, this);
        super.destroy(options);
    }

    public override addChild<U extends DisplayObject[]>(...children: U): U[0] {
        children.forEach((child) => {
            if (this.isFocusable(child)) {
                const removedChildren = this.focusedChildren.removeChildren();
                this.bluredChildren.addChild(...removedChildren);
            } 
            this.focusedChildren.addChild(child);
        });
        return children[0];
    }
    // One shot blur. Do not remove.
    /*private doBlur(): void {
        this.bluredChildren.filters = [new BlurFilter(10)];
        const size = this.canvasScaler.getSize();
        const bounds = new Rectangle(0, 0, this.fixedWidth, this.fixedHeight);
        const texture = this.renderer.generateTexture(this.bluredChildren, { region: bounds, resolution: 1 });
        const focus = new Sprite(texture);
        this.bluredChildren.filters = null;
        this.bluredChildren.addChild(focus);
    }*/

    public override removeChild<U extends DisplayObject[]>(...children: U): U[0] {
        if (this.focusedChildren.removeChild(...children) == null) {
            this.bluredChildren.removeChild(...children);
        }
        if (this.focusedChildren.children.length == 0) {
            if (this.bluredChildren.children.length == 0) {
                return children[0];
            }
            let lastIndexOfFocused: number;
            const bluredChildren = this.bluredChildren.children;
            for (let i = bluredChildren.length - 1; i >= 0; i--) {
                if (this.isFocusable(bluredChildren[i])) {
                    lastIndexOfFocused = i;
                    break;
                }
            }
            const childrenToMove = isNaN(lastIndexOfFocused) ? this.bluredChildren.removeChildren() : this.bluredChildren.removeChildren(lastIndexOfFocused);
            this.focusedChildren.addChild(...childrenToMove);
        }
        return children[0];
    }

    public override calculateBounds(): void {
        this._bounds.clear();
        this._calculateBounds();
        this._bounds.updateID = this._boundsID;
    }

    protected override _calculateBounds(): void {
        this._bounds.addFramePad(0, 0, this.fixedWidth, this.fixedHeight, 0, 0);
    }

    private onResize(size: Scales): void {
        this.width = size.width;
        this.height = size.height;
        this.bluredChildren.filterArea = new Rectangle(0, 0, this.canvasScaler.browserWidth, this.canvasScaler.browserHeight);
        this.position.set(Math.floor((this.canvasScaler.browserWidth - this.width) * 0.5), -this.canvasScaler.safeAreaOffset);
    }

    private isFocusable(obj: any): obj is IFocusable {
        return "focusable" in obj;
    }
}